import { useEffect, useRef, useState } from 'react';
import { CheckSquare, ChevronDown, Trash2, X } from 'lucide-react';

const RSVP_OPTIONS = ['invited', 'confirmed', 'declined', 'maybe'] as const;
const SIDE_OPTIONS = ['bride', 'groom', 'mutual', 'other'] as const;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export interface GuestBulkActionBarProps {
  selectedCount: number;
  onClear: () => void;
  onSetRsvp: (status: (typeof RSVP_OPTIONS)[number]) => void;
  onSetSide: (side: (typeof SIDE_OPTIONS)[number] | null) => void;
  onSetFamilyGroup: (group: string | null) => void;
  onSetTable: (tableNumber: number | null) => void;
  onDelete: () => void;
  /** Disable all action buttons while a mutation is in flight. */
  busy?: boolean;
}

/**
 * Floating action bar that appears when one or more guest rows are selected
 * on the guest list. Sticky at the top of the table on desktop and as a
 * bottom sheet on mobile so it never scrolls out of reach.
 */
export function GuestBulkActionBar({
  selectedCount,
  onClear,
  onSetRsvp,
  onSetSide,
  onSetFamilyGroup,
  onSetTable,
  onDelete,
  busy = false,
}: GuestBulkActionBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div
      data-testid="bulk-action-bar"
      className={[
        'z-30 bg-white border border-primary-200 shadow-md',
        // Desktop: sticky at the top of the scrolling content area.
        'sm:sticky sm:top-2 sm:rounded-lg sm:px-4 sm:py-2 sm:flex sm:items-center sm:gap-3 sm:mb-4',
        // Mobile: pinned bottom sheet so it never scrolls off.
        'fixed bottom-0 left-0 right-0 sm:static px-3 py-3 sm:py-2 rounded-t-lg sm:rounded-lg',
        'flex flex-wrap items-center gap-2',
      ].join(' ')}
    >
      <span className="flex items-center gap-2 text-sm text-primary-700 font-medium">
        <CheckSquare size={16} />
        {selectedCount} selected
      </span>

      <div className="flex flex-wrap items-center gap-2 ml-auto">
        <ActionDropdown
          label="Set RSVP"
          disabled={busy}
          options={RSVP_OPTIONS.map((v) => ({ value: v, label: cap(v) }))}
          onPick={(v) => onSetRsvp(v as (typeof RSVP_OPTIONS)[number])}
          testId="bulk-set-rsvp"
        />
        <ActionDropdown
          label="Set Side"
          disabled={busy}
          options={[
            ...SIDE_OPTIONS.map((v) => ({ value: v, label: cap(v) })),
            { value: '__null__', label: 'Clear' },
          ]}
          onPick={(v) => onSetSide(v === '__null__' ? null : (v as (typeof SIDE_OPTIONS)[number]))}
          testId="bulk-set-side"
        />
        <FamilyGroupAction onApply={onSetFamilyGroup} disabled={busy} />
        <TableAction onApply={onSetTable} disabled={busy} />
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-700 text-sm rounded-md hover:bg-red-50 disabled:opacity-50"
          data-testid="bulk-delete"
        >
          <Trash2 size={14} />
          Delete
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
          data-testid="bulk-clear"
        >
          <X size={14} />
          Clear
        </button>
      </div>
    </div>
  );
}

interface ActionDropdownProps {
  label: string;
  disabled?: boolean;
  options: Array<{ value: string; label: string }>;
  onPick: (value: string) => void;
  testId: string;
}

function ActionDropdown({ label, disabled, options, onPick, testId }: ActionDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid={testId}
        className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
      >
        {label}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 sm:left-0 sm:right-auto bottom-full sm:bottom-auto sm:top-full mb-1 sm:mb-0 sm:mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-40"
          role="menu"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="menuitem"
              onClick={() => {
                onPick(opt.value);
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              data-testid={`${testId}-option-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TableAction({
  onApply,
  disabled,
}: {
  onApply: (value: number | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid="bulk-set-table"
        className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
      >
        Set Table
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 sm:left-0 sm:right-auto bottom-full sm:bottom-auto sm:top-full mb-1 sm:mb-0 sm:mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-40">
          <input
            type="number"
            min={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Table number"
            data-testid="bulk-set-table-input"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2"
          />
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                onApply(null);
                setValue('');
                setOpen(false);
              }}
              className="px-2 py-1 text-xs text-gray-600 hover:underline"
              data-testid="bulk-set-table-clear"
            >
              Clear table
            </button>
            <button
              type="button"
              disabled={!value.trim() || isNaN(parseInt(value))}
              onClick={() => {
                onApply(parseInt(value));
                setValue('');
                setOpen(false);
              }}
              className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              data-testid="bulk-set-table-apply"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyGroupAction({
  onApply,
  disabled,
}: {
  onApply: (value: string | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid="bulk-set-family"
        className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
      >
        Set Family Group
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 sm:left-0 sm:right-auto bottom-full sm:bottom-auto sm:top-full mb-1 sm:mb-0 sm:mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-40">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Family group name"
            data-testid="bulk-set-family-input"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2"
          />
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                onApply(null);
                setValue('');
                setOpen(false);
              }}
              className="px-2 py-1 text-xs text-gray-600 hover:underline"
              data-testid="bulk-set-family-clear"
            >
              Clear group
            </button>
            <button
              type="button"
              disabled={!value.trim()}
              onClick={() => {
                onApply(value.trim());
                setValue('');
                setOpen(false);
              }}
              className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              data-testid="bulk-set-family-apply"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
